import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Package, TrendingUp, Clock, DollarSign, ArrowRight, CheckCircle, Settings, BarChart3, GitCompare, Sparkles, PlayCircle } from 'lucide-react';

export const Dashboard = () => {
  const { API, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalProductos: 0,
    horasDisponibles: 0,
    periodo: 'mensual'
  });
  const [visibleSteps, setVisibleSteps] = useState([]);

  useEffect(() => {
    cargarDatos();
    // Animar pasos secuencialmente
    const timer = setTimeout(() => {
      setVisibleSteps([0]);
    }, 300);
    const timers = [1, 2, 3, 4].map((step, idx) => 
      setTimeout(() => setVisibleSteps(prev => [...prev, step]), 300 + (idx + 1) * 200)
    );
    return () => {
      clearTimeout(timer);
      timers.forEach(t => clearTimeout(t));
    };
  }, []);

  const cargarDatos = async () => {
    try {
      const [productosRes, configRes] = await Promise.all([
        axios.get(`${API}/productos`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/configuracion`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setStats({
        totalProductos: productosRes.data.length,
        horasDisponibles: configRes.data.horas_disponibles,
        periodo: configRes.data.periodo
      });
    } catch (error) {
      console.error('Error al cargar datos:', error);
    }
  };

  return (
    <Layout>
      <div className="page-header">
        <h1 className="page-title" data-testid="dashboard-title">Dashboard</h1>
        <p className="page-subtitle">Resumen de tu configuración actual</p>
      </div>

      <div className="page-body">
        <div className="stat-grid">
          <Card className="stat-card" data-testid="stat-productos">
            <div className="stat-icon icon-primary">
              <Package size={24} />
            </div>
            <div className="stat-label">Productos Configurados</div>
            <div className="stat-value">{stats.totalProductos}</div>
          </Card>

          <Card className="stat-card" data-testid="stat-horas">
            <div className="stat-icon icon-success">
              <Clock size={24} />
            </div>
            <div className="stat-label">Horas Disponibles Mensuales</div>
            <div className="stat-value">{stats.horasDisponibles}h</div>
            <p className="text-sm text-gray-500 mt-2">Por mes</p>
          </Card>

          <Card className="stat-card" data-testid="stat-optimizacion">
            <div className="stat-icon icon-warning">
              <TrendingUp size={24} />
            </div>
            <div className="stat-label">Estado de Optimización</div>
            <div className="stat-value text-lg">
              {stats.totalProductos > 0 ? 'Listo' : 'Pendiente'}
            </div>
          </Card>
        </div>

        <Card className="card overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 p-6 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 animate-pulse"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24 animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm animate-bounce">
                  <Sparkles size={32} className="text-white" />
                </div>
                <h2 className="text-3xl font-bold">Bienvenido a MixOptimo</h2>
              </div>
              <p className="text-white/90 text-lg">
                Optimiza tu mix de servicios educativos y maximiza tu beneficio con análisis inteligente
              </p>
            </div>
          </div>

          <div className="p-6">
            <div className="mb-6">
              <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                <PlayCircle size={24} className="text-purple-600" />
                Recorrido Lógico de la Aplicación
              </h3>
              <p className="text-gray-600">
                Sigue estos pasos en orden para aprovechar al máximo MixOptimo
              </p>
            </div>

            <div className="space-y-4">
              {[
                {
                  step: 1,
                  icon: Package,
                  title: 'Configura tus Productos',
                  description: 'Define tus servicios educativos: clases individuales, grupales, talleres, etc. Establece precios, duración, costes variables y límites de capacidad.',
                  route: '/productos',
                  color: 'from-blue-500 to-cyan-500',
                  bgColor: 'bg-blue-50',
                  borderColor: 'border-blue-200'
                },
                {
                  step: 2,
                  icon: Settings,
                  title: 'Configura Gastos Fijos e Impuestos',
                  description: 'En el Optimizador, define tus gastos fijos mensuales (alquiler, servicios, seguros) y configura los impuestos directos (Modelo 130) para cálculos precisos.',
                  route: '/optimizador',
                  color: 'from-purple-500 to-pink-500',
                  bgColor: 'bg-purple-50',
                  borderColor: 'border-purple-200'
                },
                {
                  step: 3,
                  icon: TrendingUp,
                  title: 'Optimiza tu Mix',
                  description: 'Calcula la combinación óptima de servicios que maximiza tu beneficio neto respetando tus horas disponibles. Ajusta manualmente y visualiza resultados en tiempo real.',
                  route: '/optimizador',
                  color: 'from-green-500 to-emerald-500',
                  bgColor: 'bg-green-50',
                  borderColor: 'border-green-200'
                },
                {
                  step: 4,
                  icon: BarChart3,
                  title: 'Crea Proyecciones',
                  description: 'Genera proyecciones financieras mes a mes desde el mix optimizado. Visualiza ingresos, costes, tesorería y liquidaciones de impuestos con gráficos profesionales.',
                  route: '/proyecciones',
                  color: 'from-orange-500 to-red-500',
                  bgColor: 'bg-orange-50',
                  borderColor: 'border-orange-200'
                },
                {
                  step: 5,
                  icon: GitCompare,
                  title: 'Compara Escenarios',
                  description: 'Compara múltiples proyecciones para analizar diferentes escenarios y tomar decisiones informadas sobre tu estrategia de negocio.',
                  route: '/proyecciones',
                  color: 'from-indigo-500 to-purple-500',
                  bgColor: 'bg-indigo-50',
                  borderColor: 'border-indigo-200'
                }
              ].map((paso, index) => {
                const Icon = paso.icon;
                const isVisible = visibleSteps.includes(index);
                
                return (
                  <div
                    key={paso.step}
                    className={`
                      group relative overflow-hidden rounded-lg border-2 transition-all duration-500 transform
                      ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}
                      ${paso.bgColor} ${paso.borderColor}
                      hover:shadow-lg hover:scale-[1.02] cursor-pointer
                    `}
                    style={{ transitionDelay: `${index * 100}ms` }}
                    onClick={() => navigate(paso.route)}
                  >
                    {/* Línea conectora */}
                    {index < 4 && (
                      <div className="absolute left-8 top-full w-0.5 h-4 bg-gradient-to-b from-gray-300 to-transparent z-0"></div>
                    )}
                    
                    <div className="p-5 flex items-start gap-4 relative z-10">
                      {/* Número y icono */}
                      <div className="relative flex-shrink-0">
                        <div className={`
                          w-16 h-16 rounded-full bg-gradient-to-br ${paso.color}
                          flex items-center justify-center text-white font-bold text-xl
                          shadow-lg transform transition-transform duration-300 group-hover:scale-110
                        `}>
                          {paso.step}
                        </div>
                        {/* Icono orbitando alrededor del círculo */}
                        <div className="absolute top-0 left-0 w-16 h-16 animate-spin-slow">
                          <div className="absolute top-0 left-1/2 w-6 h-6 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white flex items-center justify-center shadow-md">
                            <div className="animate-spin-reverse">
                              <Icon size={14} className="text-gray-700" />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Contenido */}
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-lg font-bold text-gray-900 group-hover:text-purple-700 transition-colors">{paso.title}</h4>
                          <ArrowRight 
                            size={20} 
                            className="text-gray-400 group-hover:text-purple-600 group-hover:translate-x-1 transition-all duration-300"
                          />
                        </div>
                        <p className="text-gray-700 text-sm leading-relaxed">{paso.description}</p>
                      </div>
                    </div>

                    {/* Efecto de brillo al hover */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] pointer-events-none"></div>
                  </div>
                );
              })}
            </div>

            {/* Resumen final */}
            <div className="mt-8 p-6 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <CheckCircle size={32} className="text-green-600" />
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2 text-gray-900">¿Listo para comenzar?</h4>
                  <p className="text-gray-700 text-sm mb-4">
                    Comienza configurando tus productos. Cada paso te guiará hacia la optimización de tu negocio educativo.
                  </p>
                  <Button 
                    onClick={() => navigate('/productos')}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg transform transition-transform hover:scale-105"
                  >
                    <Package size={18} className="mr-2" />
                    Comenzar con Productos
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Estilos CSS para animaciones personalizadas */}
      <style>{`
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes spin-reverse {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(-360deg);
          }
        }
        
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
        
        .animate-spin-reverse {
          animation: spin-reverse 8s linear infinite;
        }
      `}</style>
    </Layout>
  );
};

export default Dashboard;
